<template>
  <v-card :loading="isBusy">
    <v-toolbar>
      <v-toolbar-title>{{ pageTitle }} - {{ form.contentKey }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip
        bottom
        v-if="isEditing && ($privilege.hasPrivilege('CONTENT_CREATE') || $privilege.hasPrivilege('CONTENT_UPDATE'))"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mr-2"
            fab
            small
            @click="
              onSubmit();
              isEditing = false;
            "
            v-bind="attrs"
            :disabled="!valid"
            v-on="on"
            :loading="isBusy"
          >
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Save") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!isEditing && tab != 3 && $privilege.hasPrivilege('CONTENT_UPDATE')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mr-2"
            fab
            small
            @click="isEditing = true"
            v-bind="attrs"
            :disabled="!valid"
            v-on="on"
            :loading="isBusy"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Edit") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="isEditing && !isNew">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mr-2"
            fab
            small
            @click="
              fetchData();
              isEditing = false;
            "
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Cancel") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!isNew && $privilege.hasPrivilege('CONTENT_DELETE')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="mr-2" fab small v-bind="attrs" v-on="on" @click="onDelete" :disabled="isEditing">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Delete") }}</span>
      </v-tooltip>
    </v-toolbar>
    <ApiError :errors="errors"></ApiError>

    <v-row v-if="uploads.existing.length">
      <v-col class="my-4 mx-4 pb-0">
        <v-alert border="top" type="info">
          Upload a file and reference it in the content HTML. Example:
          <code>&lt;a href="here upload file link" target="_blank"&gt;Download File&lt;/a&gt;</code>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-form v-model="valid">
          <v-card-text>
            <v-text-field
              label="Content Key"
              v-model="form.contentKey"
              :disabled="!isEditing"
              :rules="rules.contentKey"
              class="required"
            />
            <v-text-field label="Description" v-model="form.description" :disabled="!isEditing" />

            <ParticipantTypesField
              showKey
              v-model="selectedParticipantTypes"
              :label="$i18n.translate('Participant Types')"
              :disabled="!isEditing"
            />

            <UploadField
              v-model="uploads"
              outlined
              dense
              label="Upload File"
              hint="Upload a file and use its link in the content."
              persistent-hint
              :allowedfileExtensions="['pdf']"
              :disabled="!isEditing"
              :allowCopyUrl="true"
            ></UploadField>

            <ContentEditor
              label="Body"
              class="required"
              v-model="form.body"
              :disabled="!isEditing"
              :rules="rules.body"
            />
          </v-card-text>
        </v-form>
      </v-col>
      <v-divider vertical class="mx-4"></v-divider>
      <v-col>
        <h2>Preview</h2>
        <v-divider horizontal></v-divider>
        <div v-html="form.body"></div>
      </v-col>
    </v-row>
  </v-card>
</template>
<script>
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import ValidationService from "@/gapp-components/services/validation.service.js";
import { mapGetters } from "vuex";
import ContentEditor from "./ContentEditor.vue";
import ParticipantTypesField from "@/gapp-components/components/fields/ParticipantTypesField.vue";
import UploadField from "@/gapp-components/components/fields/UploadField.vue";

export default {
  name: "Content",
  props: {
    pageTitleKey: {
      type: String,
      default: "Content"
    }
  },
  components: {
    ApiError,
    ContentEditor,
    ParticipantTypesField,
    UploadField
  },
  metaInfo: {
    title: "Content"
  },
  data() {
    return {
      form: {
        contentKey: "",
        body: "",
        description: ""
      },
      selectedParticipantTypes: [],
      uploads: { existing: [], deleted: [] },
      isNew: true,
      isEditing: false,
      valid: true,
      isBusy: false,
      rules: {
        contentKey: ValidationService.generateTextValidationRules("Content key"),
        body: [v => !!v || "Body is required"]
      },
      errors: {},
      page: 1,
      tab: 0
    };
  },
  mounted() {
    this.isNew = this.$route.params.id == 0 ? true : false;

    if (!this.isNew) {
      this.fetchData();
    } else {
      this.isEditing = true;
      this.emitContentLoaded();
    }
  },
  computed: {
    ...mapGetters(["selectedProgram"]),
    pageTitle() {
      return this.$i18n.translate(this.pageTitleKey);
    }
  },
  methods: {
    emitContentLoaded() {
      this.$emit("content-loaded", {
        contentKey: this.form.contentKey,
        isNew: this.isNew
      });
    },
    async onSubmit() {
      this.isBusy = true;
      let postForm = Object.assign({}, this.form);
      this.errors = {};

      if (this.selectedParticipantTypes) {
        postForm.participantTypes = this.selectedParticipantTypes.map(participantType =>
          this.$api.getSelfUrl("participantTypes", participantType)
        );
      }
      const uploads = this.uploads.existing.map(upload => this.$api.getSelfUrl("uploads", upload));
      const removedUploads = [...(this.uploads.deleted || [])];

      try {
        if (this.isNew) {
          postForm.program = this.$api.getSelfUrl("programs", this.selectedProgram);
          postForm.uploads = uploads;
          let { data } = await this.$api.post("/api/contents", postForm);
          this.$router.push({ name: "content", params: { id: data.id } });
        } else {
          let patchForm = {
            contentKey: postForm.contentKey,
            description: postForm.description,
            body: postForm.body,
            participantTypes: postForm.participantTypes,
            uploads
          };

          let { data } = await this.$api.patch("/api/contents/" + this.$route.params.id, patchForm);
          this.form = data;
        }

        for (const upload of removedUploads) {
          if (upload && upload.id) {
            await this.$api.delete("/api/uploads/remove/" + upload.id);
          }
        }
        this.uploads.deleted = [];

        this.isBusy = false;
        this.fetchData();
      } catch (error) {
        let apiError = {};
        if (error.response && error.response.status === 409) {
          apiError.message = `A content with the key '${this.form.contentKey}' already exists.`;
          apiError.status = error.response.status;
        } else {
          apiError = this.$api.getErrorsFromResponse(error);
        }

        this.errors = apiError;
        this.isBusy = false;
      }
    },

    fetchData() {
      this.isBusy = true;
      this.$api
        .get("/api/contents/" + this.$route.params.id)
        .then(async ({ data }) => {
          this.form = data;
          const response = await this.$api.getRelatedObject("participantTypes", data);

          this.selectedParticipantTypes = response.data?._embedded?.participantTypes || [];
          this.$api.getRelatedObject("uploads", data).then(({ data }) => {
            this.uploads.existing = data._embedded.uploads;
            this.uploads.deleted = [];
          });
        })
        .then(() => {
          this.emitContentLoaded();
          this.isBusy = false;
        });
    },
    async onDelete() {
      this.isBusy = true;

      try {
        const uploads = [...(this.uploads?.existing || []), ...(this.uploads?.deleted || [])];

        for (const upload of uploads) {
          await this.$api.delete("/api/uploads/remove/" + upload.id);
        }

        await this.$api.delete("/api/contents/" + this.$route.params.id);

        this.$router.push({ name: "contents" });
      } catch (error) {
        this.isBusy = false;
        this.isEditing = false;
        this.errors = this.$api.getErrorsFromResponse(error);
      }
    }
  }
};
</script>
